import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Mail, MapPin, Clock, User, Phone, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim(),
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Message sent. We'll get back to you during business hours.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || "Please check your input.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send message. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        breadcrumb="Contact"
        title="Get in touch with CNL Tech."
        subtitle="Questions about a product, condition grade, or an order? Send us a message and our team will respond during business hours."
      />

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="border border-gray-200 rounded-sm p-7 md:p-10 bg-white">
              <h2 className="text-2xl font-bold tracking-tight text-[#0B192C]">
                Send us a message
              </h2>
              <p className="mt-2 text-[14px] text-gray-600">
                Fields marked with <span className="text-[#D97706]">*</span> are required.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Full Name *"
                    icon={User}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    testid="contact-form-name-input"
                    required
                  />
                  <Field
                    label="Email Address *"
                    icon={Mail}
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    testid="contact-form-email-input"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Phone (optional)"
                    icon={Phone}
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+1 (555) 555-0000"
                    testid="contact-form-phone-input"
                  />
                  <Field
                    label="Subject *"
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Order question, compatibility, etc."
                    testid="contact-form-subject-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-[0.16em] text-[#1E3E62] mb-2">
                    Message *
                  </label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share as much detail as you can — order number, product, and what you need help with."
                    data-testid="contact-form-message-input"
                    required
                    className="w-full rounded-sm border border-gray-300 bg-white px-4 py-3 text-[14.5px] text-[#0B192C] placeholder:text-gray-400 focus:border-[#0B192C] focus:outline-none focus:ring-2 focus:ring-[#0B192C]/10 transition-colors resize-y"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-form-submit-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#0B192C] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1E3E62] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send Message <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Info & map */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="border border-gray-200 rounded-sm p-7 bg-[#F9FAFB]">
              <h3 className="text-[18px] font-bold tracking-tight text-[#0B192C]">
                CNL Tech
              </h3>
              <p className="text-[13px] text-gray-600 mt-1">
                Independent Electronics Reseller
              </p>

              <div className="mt-6 space-y-4 text-[14px] text-gray-700">
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="text-[#2563EB] mt-0.5 shrink-0" />
                  <div>
                    11762 De Palma Rd Ste 1C
                    <br />
                    Corona, CA 92883-4010
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={16} className="text-[#2563EB] mt-0.5 shrink-0" />
                  <div>
                    Monday – Friday
                    <br />
                    9:00 AM – 5:00 PM PT
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={16} className="text-[#2563EB] mt-0.5 shrink-0" />
                  <a
                    href="mailto:cnltech@proton.me"
                    className="cnl-link"
                    data-testid="contact-email-link"
                  >
                    cnltech@proton.me
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={16} className="text-[#2563EB] mt-0.5 shrink-0" />
                  <a
                    href="tel:+19514792470"
                    className="cnl-link"
                    data-testid="contact-phone-link"
                  >
                    +1 (951) 479-2470
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <User size={16} className="text-[#2563EB] mt-0.5 shrink-0" />
                  <div>Owner: Marcelo Peñaloza</div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-sm overflow-hidden">
              <iframe
                title="CNL Tech location map"
                src="https://www.google.com/maps?q=11762%20De%20Palma%20Rd%20Ste%201C%2C%20Corona%2C%20CA%2092883&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-map-iframe"
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, icon: Icon, testid, ...rest }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold uppercase tracking-[0.16em] text-[#1E3E62] mb-2">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon
            size={15}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        )}
        <input
          data-testid={testid}
          {...rest}
          className={`w-full rounded-sm border border-gray-300 bg-white px-4 ${
            Icon ? "pl-10" : ""
          } py-3 text-[14.5px] text-[#0B192C] placeholder:text-gray-400 focus:border-[#0B192C] focus:outline-none focus:ring-2 focus:ring-[#0B192C]/10 transition-colors`}
        />
      </div>
    </div>
  );
}
